/**
 * @file THE SPRINT — one bar, one latch, and the six seconds in between.
 *
 * Before this file the player had exactly one speed on foot: `Player#maxSpeed`,
 * 6.5 m/s, for ever. This is the state machine that gives Shift something to
 * spend — a bar that drains while it is held, refills while it is not, and a
 * latch at the bottom that says "you are winded" until it has recovered enough
 * to be worth pressing again. That is all it is. It does not move the player,
 * it does not read the keyboard, it does not know what a car or a door is, and
 * it does not decide whether sprinting is ALLOWED.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * NO THREE.JS, NO ENGINE, NO GAME
 * ─────────────────────────────────────────────────────────────────────────────
 * Zero imports. The `Weapons.js` / `Wallet.js` / `VehicleDamage.js` mould: the
 * bus is INJECTED, it may be `null`, and every branch below is provable in node
 * with no GPU and no browser. `bus: null` is a legal, fully-stepping stamina
 * system that simply publishes nothing.
 *
 * It draws no randomness at all — rule 4 is satisfied by having nothing to
 * satisfy. There is no `Math.random()` here and no seeded stream either: a bar
 * is arithmetic.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠ IT IS NOT GIVEN THE `Player`, AND THAT IS THE POINT
 * ─────────────────────────────────────────────────────────────────────────────
 * The `Weapons.js` rule, restated because it is the same rule. The moment this
 * file reads `player.dead` — or `driving.inCar`, or `interiors.plan` — it owns a
 * SECOND gate on whether a sprint may happen, and that gate can disagree with
 * the one the caller already has. Two gates on one action is the
 * `hurtPlayer(amount, lethal)` family of bug this project has already paid for
 * once. So the caller answers ONE question — *is the player asking to sprint,
 * and does gameplay permit it right now* — and passes the answer in as `wants`.
 * This module answers the other one: *is there anything left in the bar*.
 *
 * ⚠ **THAT IS WHY THERE IS NO BRANCH BELOW THAT KNOWS WHAT A CAR IS.** Driving,
 * indoors, dead, frozen under a shop panel, standing still with no input, a
 * cutscene, a phone call — none of them is a special case in this file. Every
 * one of them is `wants === false`, and `wants === false` regenerates. A list of
 * exemptions here would be a second copy of the caller's rules that drifts the
 * first time somebody adds a fifth overlay.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FOUR NUMBERS, AND WHY THERE ARE ONLY FOUR
 * ─────────────────────────────────────────────────────────────────────────────
 * • **THE RATES ARE DERIVED, NEVER TABLED.** {@link STAMINA} holds DURATIONS —
 *   `sprintTime` and `refillTime` — because durations are what was decided and
 *   what a playtest report is phrased in ("it runs out too fast"). The rates are
 *   `1 / sprintTime` and `1 / refillTime`, computed once below. A third constant
 *   holding a rate is the `docs/vehicles.md` bail-out-window trap (`fireAt *
 *   maxHp / burnDps` deliberately has no third constant for the 10 s window) and
 *   the `docs/drone.md` `lowAt` trap: two numbers that must agree, with nothing
 *   making them agree.
 *
 * • **`sprintTime` 6 s IS ONE CITY BLOCK.** At `Player#maxSpeed` 6.5 m/s times
 *   {@link STAMINA}`.mul` 1.4 the player covers 9.1 m/s, so a full bar is
 *   `9.1 * 6 = 54.6` m — against Kingsmouth's `roads.streetSpacing` of **54 m**
 *   (`levels/city/config.js`; Bayside's is 48, so the margin is wider there).
 *   One press of Shift crosses one block and lands you at the next junction with
 *   nothing left. That is the sentence the number encodes.
 *   ⚠ **THE 54 IS WRITTEN OUT BY HAND HERE ON PURPOSE.** An engine system may
 *   not import a level constant — `src/engine/` never reaches into
 *   `src/levels/`, and a `import { CITY } from '../../levels/city/config.js'`
 *   would be the only such reference in the tree. The arithmetic is documented
 *   instead of imported, exactly as `docs/weapons.md`'s `PLANT_FRAC` mirror is.
 *
 * • **`refillTime` 9 > `sprintTime` 6, AT 1.5×, ON PURPOSE.** The refill is
 *   slower than the spend, which is the whole economy: sustained Shift-holding
 *   nets you a WALK with sprint bursts in it, not a permanent run. If the two
 *   were equal the bar would be a 50% duty cycle and the sprint would be free at
 *   half strength; if the refill were faster, Shift would be a button with no
 *   cost and the bar would be decoration. `tests/stamina.test.mjs` pins the
 *   INEQUALITY rather than the two numbers, so retuning either still has to keep
 *   the relationship.
 *   ⚠⚠ **AND IT IS THIS RATIO — NOT THE LATCH — THAT SETS THE LONG-RUN SPEED.**
 *   A bar is bounded, so over any long stretch what is spent must equal what is
 *   regained: `s / sprintTime = (T - s) / refillTime`, which solves to a
 *   sustainable sprint duty of `sprintTime / (sprintTime + refillTime)` =
 *   **6/15 = 40%**, i.e. a long-run mean speed of `1 + 0.4 * (mul - 1)` =
 *   **1.16 × walk**. That bound holds for EVERY input pattern and for a model
 *   with the latch removed — it is arithmetic, not policy. Do not reach for the
 *   latch to change the average; reach for these two durations. `sprintTime` is
 *   the only one of the four numbers that also fixes a DISTANCE, so
 *   `refillTime` is the lever for the average and `sprintTime` is not.
 *
 * • ⚠⚠ **`mul` 1.4 IS DEFENSIBLE BUT UNPLAYTESTED — `bulletToCar` 0.35's
 *   status, and every feel number in `docs/drone.md`.** NOBODY HAS RUN A STEP OF
 *   IT. Two things can honestly be said for it and neither is a derivation:
 *   **(1)** 6.5 × 1.4 = 9.1 m/s is **54% of `DRIVE.topSpeed` 17**, so a car stays
 *   clearly worth taking — a sprint that approached the car's speed would delete
 *   the vehicle layer, which is the one bound on this constant that is not a
 *   matter of taste. **(2)** It is half of what makes `sprintTime` 6 s land on
 *   one 54 m block: the two numbers were chosen together and neither survives
 *   the other moving alone. Everything past that is a guess, and the first
 *   playtest outranks it.
 *   ⚠ **AND IT IS NOT DERIVED FROM THE CHASE TARGET, WHICH IS THE OBVIOUS WRONG
 *   READING.** `game/missions/sideJobs.mission.js`'s runner carries
 *   `speedMul: 1.4` as well; they are the SAME DIGITS AND NOTHING ELSE. That one
 *   scales a gang member's authored **3.2** m/s (→ 4.5) and was tuned as a
 *   CLOSING RATE against a WALKING player; this one scales the player's **6.5**.
 *   Neither is derived from the other and moving one must NOT move the other.
 *   (A WALKING player already closes that chase at 6.5 − 4.5 = 2 m/s — the
 *   mission's own browser-measured ~13 s — so "you need the sprint to catch him"
 *   is false. What the sprint changes is the closing rate: 9.1 − 4.5 = 4.6 m/s,
 *   the same 26 m in ~6 s, i.e. exactly one bar. The ~13 s is now the UPPER
 *   bound, not the duration; the mission file owns that note.)
 *   ⚠⚠ **AND IT CARRIES A CEILING NOTHING ELSE EXPRESSES — 1.7 — SET BY THE
 *   ESCORT.** `Followers.js#FOLLOW.sprintMul` 2.6 puts a follower at
 *   `3.2 × 2.6 = 8.32` m/s, and an escort that cannot outrun the player trails
 *   out to `FOLLOW.warpDist` 55 and teleports for ever. ⚠ **THE COMPARISON IS
 *   AGAINST THE SUSTAINED SPEED, NOT THE SPRINT** — that is the whole subtlety,
 *   and it is the duty-cycle bound above doing the work: the player cannot hold
 *   9.1 m/s, they hold `6.5 × 1.16 = 7.54` m/s, and 8.32 beats THAT by
 *   **+0.78 m/s**. Solved for our own constant the invariant
 *   `escort > walk × (1 + duty × (mul − 1))` becomes
 *   `mul < 1 + (8.32/6.5 − 1) / 0.4` = **1.7**, so 1.4 sits about 21% under a
 *   real ceiling. Past it an escort can never regain station however long the
 *   player walks — a bound on THIS module's constant, not on the follower's.
 *   ⚠ **A READER COMPUTING THE INSTANTANEOUS RATE WILL CONCLUDE THE ESCORT IS
 *   BROKEN, AND THEY WILL BE WRONG.** 8.32 against 9.1 is **−0.78 m/s** and it
 *   is EXPECTED AND FINE: the follower loses ~4.7 m over a full 6 s burst and
 *   takes it back at 1.82 m/s during the recovery walk — ~2.6 s of the 3.15 s
 *   the exhaustion latch enforces, so it is square before the player can sprint
 *   again, and 4.7 m is nowhere near `warpDist`. That mis-reading cost this
 *   round a wrong constant (`sprintMul` was briefly moved to 3.4 to "fix" it,
 *   which also tripled the closing rate against a WALKING player, 1.82 →
 *   4.38 m/s, changing the one case that had actually been measured in a
 *   browser). `tests/stamina.test.mjs` (a) asserts the SUSTAINED rate and the
 *   ceiling, cross-file and computed from live reads, so neither the mirror nor
 *   the mis-reading can come back in silence.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ THE EXHAUSTION LATCH IS THE ONE MECHANISM THAT CLOSES TAP-ABUSE
 * ─────────────────────────────────────────────────────────────────────────────
 * At zero the bar LATCHES: {@link createStamina.sprinting} stays false until
 * `value >= STAMINA.recoverFrac`, **even while the player is still holding
 * Shift**. Without it, a player who taps Shift on and off keeps the bar hovering
 * a hair above zero and is granted a sprint step every time it ticks above it —
 * the bar empties and then simply never matters again, because "empty" is a
 * state they leave and re-enter several times a second. **The measured shape of
 * an unlatched model under a 0.1 s on / 0.1 s off tap is a walk that is never
 * longer than 0.12 s** (`tests/stamina.test.mjs` (d)): the player never walks
 * and never sprints, they smear, and the bar reads as broken rather than as a
 * cost. The latch converts that shimmer into BLOCKS — a real walk of at least
 * `recoverFrac * refillTime = 3.15` s, then a real sprint of 2.1 s.
 *
 * ⚠⚠ **THE LATCH IS AVERAGE-NEUTRAL BY CONSTRUCTION, AND A READER WILL EXPECT
 * OTHERWISE.** It is natural to describe it as "the thing that stops tap-abuse
 * paying", and to write a test asserting the unlatched model runs FASTER. It
 * does not, and it cannot: both models converge on the same
 * `sprintTime / (sprintTime + refillTime)` = 40% duty derived above, because a
 * bounded bar cannot pay out more than it takes in — measured 0.400 vs 0.400,
 * mean speed 1.160 vs 1.160. What the latch buys is entirely the GRAIN: 3.15 s
 * of real walking against 0.12 s, i.e. whether the bar's bottom is a state the
 * player can see and plan around or a 5 Hz flicker. Both facts are pinned in
 * (d), the second as the negative control, precisely so the next reader does not
 * "fix" the ratio while trying to make the latch do the ratio's job.
 *
 * ⚠ **A "REGEN DELAY AFTER RELEASING SHIFT" WAS CONSIDERED AND REJECTED.** It is
 * the other standard answer to tap-abuse and it does the SAME job as the latch:
 * both exist to stop a rapid on/off pattern paying. Shipping both is two knobs
 * for one job — the exact thing this project refuses (`docs/weapons.md`: a
 * second "cannot fire while moving" speed gate on top of the stance duty cycle
 * was closed as "redundant BY CONSTRUCTION and the two-places-computing bug").
 * Worse, a delay is invisible: the bar sits still for a moment after you let go
 * and the player has no way to learn why. The latch is visible — the bar is
 * empty, and it is visibly refilling to a mark. One mechanism per job.
 *
 * ⚠ 0.35 is the mark, not 1.0. Requiring a FULL bar to sprint again would mean
 * nine seconds of walking after every sprint, which at a 6 s spend is a 60%
 * duty cycle enforced in one indivisible block — the player stops pressing
 * Shift at all. 0.35 buys back 2.1 s of sprint after 3.15 s of walking, which
 * is one junction of recovery for two thirds of a block of running.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ THIS FILE IS THE SOLE PUBLISHER OF `'player:stamina'`
 * ─────────────────────────────────────────────────────────────────────────────
 * The `'player:ammo'` rule, for the same two reasons: a step-end emitter in
 * `Gameplay` cannot fire while an overlay has the fixed step frozen, and two
 * publishers on one channel is the `'hud:prompt'` flicker. Every mutator here
 * publishes SYNCHRONOUSLY, inside the call, and the payload carries the WHOLE
 * state so a subscriber that missed one event is still correct on the next.
 * It fires ONCE ON CONSTRUCTION — the `Wallet.js` rule; a HUD that renders
 * `null` until its first event would sit blank until the player's first sprint.
 *
 * ⚠ **AND IT IS QUANTISED, BECAUSE THIS ONE TICKS.** Unlike ammo or money, this
 * value changes on nearly every one of sixty steps a second, straight into
 * React. The published value is rounded to {@link PUBLISH_STEPS} (1/100 of a
 * bar) and the change test compares the ROUNDED value, so a full refill costs at
 * most ~100 events instead of ~540. ⚠ **The value that is PUBLISHED is the value
 * that was COMPARED** — that is `docs/dialogue.md`'s `ALPHA_STEPS` rule, and the
 * reason for it is the same: publish the raw number after comparing the rounded
 * one and the HUD's contents depend on which frame happened to trip the gate.
 * A flag flip (`sprinting`, `exhausted`) ALWAYS publishes, whatever the bar did.
 *
 * @module engine/systems/Stamina
 */

/**
 * The four numbers. DURATIONS and RATIOS only — see the header on why there is
 * no rate in here.
 */
export const STAMINA = Object.freeze({
  /**
   * Seconds of continuous sprint in a full bar. ONE CITY BLOCK: 6.5 m/s ×
   * `mul` 1.4 × 6 s = 54.6 m against Kingsmouth's 54 m street spacing.
   */
  sprintTime: 6,
  /**
   * Seconds from empty to full. 1.5 × {@link STAMINA}`.sprintTime` — the refill
   * is slower than the spend, so holding Shift nets a walk. The suite pins the
   * inequality, not the number.
   */
  refillTime: 9,
  /**
   * The mark the exhaustion latch releases at. 0.35 of a bar = 3.15 s of walking
   * bought back as 2.1 s of sprint.
   */
  recoverFrac: 0.35,
  /**
   * Sprint speed as a multiple of `Player#maxSpeed`. Matches the chase target's
   * authored `speedMul: 1.4`, so a sprinting player closes and a walking one
   * does not.
   */
  mul: 1.4,
});

/**
 * Quantisation of the published bar: 1/100. Two derivations, both of which have
 * to hold. **Visually**, a HUD bar is at most a few hundred pixels wide, so 1%
 * of it is sub-pixel at every size this game ships — the player cannot see a
 * finer step, so publishing one is pure cost. **On the channel**, it caps a full
 * 9 s refill at ~100 events instead of the ~540 a 60 Hz step would produce, and
 * a 6 s drain at ~100 instead of ~360.
 *
 * ⚠ It is a QUANTISATION, not a throttle: the published number is the rounded
 * one, so the HUD never receives a value the change gate did not compare. See
 * the header.
 */
export const PUBLISH_STEPS = 100;

/**
 * @param {any} n
 * @returns {boolean} A real, usable number. The `Wallet.js#usable` guard — a
 *   `NaN` dt from a stalled clock must not poison the bar.
 */
const usable = (n) => typeof n === 'number' && Number.isFinite(n);

/**
 * ⚠ DERIVED, NOT TABLED. Bar-fractions per second. There is no `drainRate` in
 * {@link STAMINA} and there must never be one — see the header.
 */
const DRAIN = 1 / STAMINA.sprintTime;
/** ⚠ DERIVED, NOT TABLED. @see DRAIN */
const REGEN = 1 / STAMINA.refillTime;

/**
 * @param {number} v
 * @returns {number} `v` in [0, 1]. ⚠ Both ends are EXACT and both are
 *   reachable: the latch tests `<= 0` and the HUD's "full" tests `>= 1`, and a
 *   bar that stops at 0.9999999 is a bar that is never full.
 */
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * The player's wind: one bar, one latch, one speed multiplier.
 *
 * @param {Object} [o]
 * @param {any} [o.bus] `'player:stamina'` `{value, sprinting, exhausted}` — on
 *   construction and on every quantised change. Null means no emits;
 *   everything else works identically.
 * @returns {Object}
 */
export function createStamina(o = {}) {
  const { bus = null } = o ?? {};

  /** THE bar, 0..1. Never outside it, and both ends are exact. */
  let value = 1;
  /** A sprint is GRANTED this step — the caller's licence to scale its speed. */
  let sprinting = false;
  /** ⚠ THE LATCH. True from the moment the bar hits 0 until it reaches
   *  {@link STAMINA}`.recoverFrac`, regardless of what the player is holding. */
  let exhausted = false;
  let disposed = false;

  // ── the counters (for `stats()`, `DebugPanel` and the suites) ─────────────
  /** Seconds actually spent sprinting, lifetime. */
  let sprintT = 0;
  /** Times the bar has been run all the way to empty. */
  let exhaustions = 0;

  // ── the publish gate ──────────────────────────────────────────────────────
  /** The last QUANTISED value put on the bus. `-1` = nothing published yet. */
  let lastQ = -1;
  let lastSprinting = false;
  let lastExhausted = false;

  /**
   * @param {number} v
   * @returns {number} `v` rounded to {@link PUBLISH_STEPS}. 0 and 1 survive
   *   EXACTLY, which is what keeps "empty" and "full" sayable on the channel.
   */
  const quant = (v) => Math.round(v * PUBLISH_STEPS) / PUBLISH_STEPS;

  /**
   * THE channel. The whole state, every time. ⚠ The `q` it is HANDED is the same
   * number the gate compared — see the header; publishing the raw `value` here
   * would make the HUD depend on which frame tripped the gate.
   * @param {number} q
   */
  const _emit = (q) => {
    lastQ = q;
    lastSprinting = sprinting;
    lastExhausted = exhausted;
    bus?.emit?.('player:stamina', { value: q, sprinting, exhausted });
  };

  /**
   * Publishes only if something a subscriber can see has moved. A flag flip
   * always counts; the bar counts only at 1/100 resolution.
   */
  const _publishIfChanged = () => {
    const q = quant(value);
    if (q === lastQ && sprinting === lastSprinting && exhausted === lastExhausted) return;
    _emit(q);
  };

  const api = {
    name: 'stamina',

    // ── readouts ─────────────────────────────────────────────────────────────

    /** @returns {number} The bar, 0..1. Exact at both ends. */
    get value() {
      return value;
    },

    /**
     * @returns {boolean} A sprint is granted THIS step. False whenever the
     *   player is not asking, and false while {@link exhausted} whether they are
     *   asking or not.
     *
     * ⚠ **`sprinting` AND `exhausted` ARE BOTH TRUE FOR EXACTLY ONE FRAME** —
     * the step that runs the bar out. It looks like a contradiction and it is
     * the grant order (see {@link update}): the licence is decided against the
     * bar as it stood at the START of the step, so a step the player had stamina
     * for is a step they get, and the latch is what refuses the NEXT one. A HUD
     * that keys off `exhausted` will therefore paint "winded" on the same frame
     * the legs are still moving, which is correct and is one 60th of a second.
     * `tests/stamina.test.mjs` (c) pins it so it does not get "fixed" into
     * charging the player for a step they were refused.
     */
    get sprinting() {
      return sprinting;
    },

    /**
     * @returns {boolean} The latch. Set the step the bar reaches 0, cleared the
     *   step it reaches {@link STAMINA}`.recoverFrac`. The HUD paints the bar
     *   differently for this, which is the whole reason the latch is visible
     *   rather than a hidden regen delay — see the header.
     */
    get exhausted() {
      return exhausted;
    },

    /**
     * ⚠ **THE ONE PLACE THIS PRODUCT IS COMPUTED.** The caller multiplies
     * `Player#maxSpeed` by this and nothing else; a second `sprinting ? 1.4 : 1`
     * anywhere in the tree is two places computing one thing.
     *
     * ⚠ It is EXACTLY `1` when not sprinting — `===`, not `≈`. The
     * `speedMul: 1` / `Player.armour: 0` idiom: an inert multiplier must be
     * bit-identical to no multiplier at all (`x * 1` is exact in IEEE-754), so a
     * player who never touches Shift walks at precisely the speed they walked at
     * before this file existed.
     *
     * @returns {number} `STAMINA.mul` while sprinting, `1` otherwise.
     */
    get speedMul() {
      return sprinting ? STAMINA.mul : 1;
    },

    // ── the clock ────────────────────────────────────────────────────────────

    /**
     * One fixed step.
     *
     * @param {number} dt Seconds. ⚠ `dt <= 0` (and a non-finite one) is a
     *   NO-OP: no drain, no regen, no emit. A frozen loop must not regenerate
     *   the bar, and a negative dt must not run it backwards.
     * @param {any} wants The player is asking to sprint AND gameplay permits it.
     *   ⚠ ONE boolean, decided by the caller — see the header. `false` is not a
     *   list of exemptions, it is the answer to the question.
     * @returns {boolean} Whether a sprint was granted, i.e. {@link sprinting}.
     *   Returned as well as exposed so the caller can read it in the same line
     *   it steps the system.
     */
    update(dt, wants) {
      if (disposed || !usable(dt) || dt <= 0) return sprinting;

      // ⚠ THE GRANT IS DECIDED BEFORE THE DRAIN, and against the bar as it stood
      // at the START of the step. The step whose drain empties the bar was still
      // a sprint step — the player really did move at sprint speed for it — so
      // grant and then charge, never the other way round.
      const grant = !!wants && !exhausted && value > 0;

      if (grant) {
        sprintT += dt;
        value = clamp01(value - DRAIN * dt);
        if (value <= 0) {
          // ⚠ EXACT 0, and the latch closes on the same step. There is no frame
          // in which the bar reads empty and the player is still being granted a
          // sprint on the next one.
          value = 0;
          if (!exhausted) exhaustions++;
          exhausted = true;
        }
      } else {
        // ⚠ EVERY non-granting step regenerates, INCLUDING one where the player
        // is holding Shift while exhausted. That is what makes the latch a
        // recovery rather than a punishment: holding the key down through the
        // walk costs nothing and the bar comes back at exactly the same rate.
        value = clamp01(value + REGEN * dt);
        if (exhausted && value >= STAMINA.recoverFrac) exhausted = false;
      }

      sprinting = grant;
      _publishIfChanged();
      return sprinting;
    },

    // ── the events somebody else causes ──────────────────────────────────────

    /**
     * The player woke up. Full bar, latch cleared, not sprinting.
     *
     * Unconditional publish, like `Weapons#onRespawn`: the HUD is coming back
     * from a death splash and must repaint whatever the numbers say, even in the
     * case where they happen to be the numbers it last painted.
     */
    onRespawn() {
      if (disposed) return;
      value = 1;
      sprinting = false;
      exhausted = false;
      _emit(quant(value));
    },

    /**
     * Forces a republish of the current state — for a HUD that mounted after
     * construction. The bus has no replay, so a widget that appears mid-session
     * has no other way to learn the bar. Unconditional by design: a gated
     * "refresh" is not a refresh.
     */
    publish() {
      if (disposed) return;
      _emit(quant(value));
    },

    /**
     * @returns {{value: number, sprinting: boolean, exhausted: boolean,
     *   sprintT: number, exhaustions: number}} A plain object for
     *   `Gameplay.stats()` and `DebugPanel`. ⚠ No `speedMul` here — it is a
     *   getter, and a copy of it in a stats blob is a second place a reader
     *   might take the multiplier from.
     */
    stats() {
      return { value, sprinting, exhausted, sprintT, exhaustions };
    },

    /**
     * Idempotent. Freezes the bar: {@link update} is a no-op afterwards and
     * nothing more is published, so a system torn down mid-frame cannot repaint
     * a HUD that is on its way out. The `Wallet#dispose` rule.
     *
     * ⚠ It does NOT drop the player back to walking speed by itself — it leaves
     * `sprinting` exactly as it was, because a disposed system has no business
     * writing one last state change into a level that is being taken apart.
     */
    dispose() {
      disposed = true;
    },
  };

  // The HUD's first paint depends on this — the `Wallet.js` rule.
  _emit(quant(value));

  return api;
}
